import { useEffect, useState } from 'react';
import { apiUrl, getApiBase } from './serverConfig';

/*
 * Which deployment the API this client talks to is: production, staging, dev
 * or local (GET /healthz's `environment`, utils.environment_name on the
 * server). Read once per API base per tab and kept in sessionStorage, with
 * plain fetch -- not utils/api.js, whose interceptors would end a session on
 * a 401 and which the navbar's tests watch for requests.
 *
 * The answer is the API's, not the web build's: a dev UI pointed at the
 * production API says production.
 */
const STORE = 'mb_environment';
export const ENVIRONMENTS = ['production', 'staging', 'dev', 'local'];

function cached(base) {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORE) || 'null');
    return saved && saved.base === base && ENVIRONMENTS.includes(saved.environment) ? saved.environment : null;
  } catch (e) {
    return null;
  }
}

function remember(base, environment) {
  try {
    sessionStorage.setItem(STORE, JSON.stringify({ base, environment }));
  } catch (e) { /* private window or blocked storage: asked again next load */ }
}

let pending = null;

/** The environment's name, or null when it cannot be told. Never rejects. */
export async function fetchEnvironment({ fetchFn = typeof fetch === 'function' ? fetch : null } = {}) {
  const base = getApiBase();
  const known = cached(base);
  if (known) return known;
  if (!fetchFn) return null;
  if (!pending) {
    pending = (async () => {
      try {
        const res = await fetchFn(apiUrl('/healthz'), { headers: { Accept: 'application/json' } });
        if (!res.ok) return null;
        const body = await res.json();
        const name = ENVIRONMENTS.includes(body?.environment) ? body.environment : null;
        if (name) remember(base, name);
        return name;
      } catch (e) {
        return null;
      } finally {
        pending = null;
      }
    })();
  }
  return pending;
}

/** The environment's name once known (null until then, and when unknown). */
export function useEnvironment() {
  const [name, setName] = useState(() => cached(getApiBase()));
  useEffect(() => {
    if (name) return undefined;
    let live = true;
    fetchEnvironment().then((found) => { if (live && found) setName(found); });
    return () => { live = false; };
  }, [name]);
  return name;
}

/** Forget what was read (tests; a changed server). */
export function resetEnvironment() {
  pending = null;
  try { sessionStorage.removeItem(STORE); } catch (e) { /* nothing kept */ }
}
